/**
 * fetch wrapper for the backend API.
 *
 * - Sends cookies (credentials: "include") so the HttpOnly session cookie works.
 * - On 401, navigates to /login (handled via window.location to escape React Router).
 * - For state-changing methods (POST/PUT/DELETE/PATCH), the Origin header is
 *   automatically attached by the browser; backend verifies scheme + netloc.
 */

export class ApiError extends Error {
  readonly status: number
  readonly detail?: unknown
  constructor(status: number, message: string, detail?: unknown) {
    super(message)
    this.name = "ApiError"
    this.status = status
    this.detail = detail
  }
}

type FetchOptions = Omit<RequestInit, "body"> & {
  body?: unknown
  searchParams?: Record<string, string | number | boolean | undefined>
}

export async function apiFetch<T = unknown>(path: string, options: FetchOptions = {}): Promise<T> {
  const { body, searchParams, headers, ...rest } = options

  const url = new URL(path, window.location.origin)
  if (searchParams) {
    for (const [k, v] of Object.entries(searchParams)) {
      if (v !== undefined && v !== null) {
        url.searchParams.set(k, String(v))
      }
    }
  }

  const finalHeaders: Record<string, string> = {
    Accept: "application/json",
    ...((headers as Record<string, string> | undefined) ?? {}),
  }

  let finalBody: BodyInit | undefined
  if (body !== undefined) {
    finalHeaders["Content-Type"] = "application/json"
    finalBody = JSON.stringify(body)
  }

  const response = await fetch(url.toString(), {
    ...rest,
    headers: finalHeaders,
    body: finalBody,
    credentials: "include",
  })

  if (response.status === 401) {
    // Login required. Don't loop redirects when already on /login.
    if (!window.location.pathname.startsWith("/login")) {
      const next = `${window.location.pathname}${window.location.search}`
      window.location.replace(`/login?next=${encodeURIComponent(next)}`)
    }
    throw new ApiError(401, "Unauthorized")
  }

  if (!response.ok) {
    let detail: unknown
    try {
      detail = await response.json()
    } catch {
      // ignore
    }
    const msg =
      (detail &&
      typeof detail === "object" &&
      "detail" in detail &&
      typeof detail.detail === "string"
        ? detail.detail
        : null) ?? `HTTP ${response.status}`
    throw new ApiError(response.status, msg, detail)
  }

  if (response.status === 204) {
    return undefined as T
  }

  const contentType = response.headers.get("content-type") ?? ""
  if (contentType.includes("application/json")) {
    return (await response.json()) as T
  }
  return (await response.text()) as unknown as T
}
